<template>
  <BaseModal
    :heading="$t('appointment.create.timeslots_modal.title')"
    @close="$emit('close')"
  >
    <BaseGrid container>
      <BaseGrid
        :size="5"
        :class="$style.calendar"
      >
        <BaseDatePicker
          v-model="date"
          inline
          :availableDates="availableDates"
          @visibleRangeChanged="setRange"
          v-test="'timeslot-picker'"
        />
      </BaseGrid>
      <BaseGrid
        :size="7"
        :class="$style.timeslotsGrid"
      >
        <Flex
          v-if="anyAvailableTimeSlots"
          direction="column"
          v-test="'available-time-slots'"
        >
          <Flex
            direction="column"
            v-for="timeslotsGroup in availableTimeSlots"
            :key="timeslotsGroup.day_part"
          >
            <BaseHeading>
              {{ $t(
                  'appointment.create.timeslots_modal.' +
                    timeslotsGroup.day_part
                ) }}
            </BaseHeading>
            <Flex
              :gap="0.5"
              wrap
            >
              <BaseButton
                v-for="timeslot in timeslotsGroup.timeslots"
                :key="timeslot"
                color="inverted"
                :selected="timeslot === selectedTimeslot"
                @click="selectTimeSlot(timeslot)"
                v-test="'time-slot'"
              >
                {{ timeslot }}
              </BaseButton>
            </Flex>
          </Flex>
          <div :class="$style.bottom">
            <BaseText
              v-show="selectTimeError && !selectedTimeslot"
              color="error"
            >
              {{ $t('appointment.create.timeslots_modal.select_time') }}
            </BaseText>
            <div :class="$style.button">
              <BaseButton
                @click="apply"
                v-test="'availability-apply'"
              >
                {{ $t('global.actions.apply') }}
              </BaseButton>
            </div>
          </div>
        </Flex>
        <div
          v-else
          :class="$style.timeslotsInner"
          v-test="'available-time-slots'"
        >
          <BaseHeading>
            {{ $t('appointment.create.timeslots_modal.no_timeslots') }}
          </BaseHeading>
        </div>
      </BaseGrid>
    </BaseGrid>
  </BaseModal>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { defineComponent, ref } from 'vue';
import dayjs from '@/dayjs';
import { combineDateTime } from '@/helpers/dates';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';

export default defineComponent({
  emits: ['close'],
  setup() {
    const store = useCreateAppointmentStore();
    const { formData } = store;
    // In series mode this is the active slot's date; otherwise it equals formData.startAt.
    const dateObj = dayjs(store.series.activeStartAt || formData.startAt);

    return {
      date: ref(dateObj.format('YYYY-MM-DD')),
      selectedTimeslot: ref(dateObj.format('HH:mm')),
      formData,
      store,
    };
  },
  data() {
    return {
      timeslotList: [],
      selectTimeError: false,
      startDate: null,
      endDate: null,
    };
  },
  apollo: {
    timeslots: {
      query: gql`
        query getTimeslots(
          $startDate: DateTime!
          $locationId: ID
          $endDate: DateTime!
          $parts: [AppointmentPartAttributes!]
        ) {
          timeslots(
            start: $startDate
            locationId: $locationId
            end: $endDate
            parts: $parts
          )
        }
      `,
      variables() {
        const parts = this.formData.partsAttributes.filter(
          (part) => !!part.serviceId,
        );
        return {
          parts,
          startDate: this.startDate,
          endDate: this.endDate,
          locationId: this.formData.locationId,
        };
      },
      skip() {
        return !this.startDate || !this.endDate;
      },
      result({ data }) {
        this.timeslotList = data.timeslots;
      },
    },
  },
  watch: {
    date() {
      this.selectedTimeslot = null;
    },
  },
  computed: {
    availableTimeSlots() {
      return this.timeslotList.find((slot) => slot.date === this.date)
        ?.timeslots;
    },
    availableDates() {
      return this.timeslotList
        .filter((slot) => !!slot.timeslots?.length)
        .map((slot) => slot.date);
    },
    anyAvailableTimeSlots() {
      return !!this.availableTimeSlots?.length > 0;
    },
  },
  methods: {
    setRange({ from, to }) {
      this.startDate = from;
      this.endDate = to;
    },
    selectTimeSlot(timeslot) {
      this.selectedTimeslot = timeslot;
    },
    apply() {
      if (this.selectedTimeslot) {
        // Writing via the series' active date routes to the right slot;
        // in single-appointment mode this is just formData.startAt.
        this.store.series.activeStartAt = combineDateTime(
          this.date,
          this.selectedTimeslot,
        );
        this.$emit('close');
      } else {
        this.selectTimeError = true;
      }
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.calendar {
  border-right: 1px solid var(--color-border);
  display: flex;
  justify-content: center;
}

.bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.button {
  margin-left: auto;
}

.timeslotsGrid {
  padding-left: 30px;
}
</style>
